import { shallow } from "zustand/shallow";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import {
    type FundDataWithType,
    type ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import { type Config, type TabConfig } from "../../../config/types";
import { useOpenShareClassGroup } from "../../../hooks/useIsOpen";
import { Translate } from "../../../hooks/useTranslation";
import dayjs from "../../../lib/dayjs";
import groupShareClasses from "../../../lib/groupShareClasses--temp";
import { generateLocalId } from "../../../store/helpers";
import useExplorerStore from "../../../store/store";
import FundCardDocuments from "../../FundCardDocuments";
import { AdditionalDocumentsSection } from "../../FundCardDocuments/components";
import { ModalKeyDocumentsSection } from "../../FundCardDocuments/components/KeyDocumentsSection";
import { DocumentContextProvider } from "../../FundCardDocuments/context";
import FundCardTable from "../../FundCardTables";
import { useFootnote } from "../../FundCardTables/hooks";
import ListingsTabView from "../../FundCardTables/views/ListingsTabView";
import Tabs from "../../Tabs";
import { FundCardProvider } from "../context";
import { FundCardAccordions } from "./FundCardAccordions";
import FundCardPricesPMC from "./FundCardPricesPMC";

export interface Props {
    fund: FundDataWithType;
    type: "pill" | "accordion";
    initialTab?: string;
    docHasUpdates?: (x: string | number) => boolean;
    markRead?: (x: string | number) => void;
}

const FundCardTabs = ({
    fund,
    type,
    initialTab,
    docHasUpdates,
    markRead,
}: Props) => {
    const isMobile = useBreakpointContext()?.currentBreakpoint === "xs";

    const { config, metadata, getShareClassById } = useExplorerStore(
        ({ config, rawData, getShareClassById }) => ({
            config,
            metadata: rawData?.metadata,
            getShareClassById,
        }),
        shallow,
    );
    const isPMC = config.productType === "pmc";

    const tabs = createTabs(config);
    const audience = config.audienceId;
    const share_classes = fund.data.share_classes;

    const scDataWithTypeGuard = (
        d: ShareClassDataWithType | undefined,
    ): d is ShareClassDataWithType => Boolean(d);
    const scDataWithType = share_classes
        .map(generateLocalId("shareclass"))
        .map((id) => getShareClassById(id))
        .filter(scDataWithTypeGuard);
    const grouped = groupShareClasses(scDataWithType);
    const openGroups = useOpenShareClassGroup(
        Object.entries(grouped).reduce(
            (acc, [key, scObjs]) => {
                return { ...acc, [key]: scObjs.map((sc) => sc.data.localId) };
            },
            {} as Record<string, string[]>,
        ),
    );
    const { tabEvent } = useYardActionEvents();
    const trackTabChange = (key: string) => {
        tabEvent({
            key,
            fund: {
                name: fund.data.data.name,
                id: fund.data.id,
                type: fund.data.type,
            },
        });
    };
    const customPillFilter = (t: TabConfig) => {
        return t.key === "performance" &&
            scDataWithType.length === 1 &&
            scDataWithType[0]?.data.id === 4509
            ? false
            : true;
    };
    const filteredTabs = tabs.filter(customPillFilter);

    switch (type) {
        case "pill": {
            const content = filteredTabs.map((tab) =>
                createTabContent({
                    config,
                    tab,
                    fund,
                    subTabs: config.tabs.filter((t) => t.parent === tab.key),
                }),
            );
            const initialTabIndex = initialTab
                ? tabs.findIndex((tab) => tab.key === initialTab) || 0
                : 0;
            return (
                <FundCardProvider
                    openGroups={openGroups as string[]}
                    fund={fund}
                >
                    <div>
                        <Tabs
                            tabs={filteredTabs.map((t) => ({
                                ...t,
                                label: <Translate>{t.label}</Translate>,
                            }))}
                            content={content}
                            onChange={(i) =>
                                trackTabChange(tabs[i]?.label ?? `tab-${i}`)
                            }
                            type="pill"
                            contentContainerClassName="relative z-30"
                            scrollerClassName="overflow-y-hidden"
                            activeIndex={initialTabIndex}
                        />
                    </div>
                </FundCardProvider>
            );
        }
        case "accordion": {
            const accordions = tabs.flatMap((tab) =>
                createAccordionContent({
                    config,
                    tab,
                    fund,
                    subTabs: config.tabs.filter((t) => t.parent === tab.key),
                }),
            );
            return (
                <FundCardProvider fund={fund}>
                    <DocumentContextProvider
                        params={{ audience, share_classes }}
                        fund={fund}
                        hasUpdates={docHasUpdates}
                        markRead={markRead}
                    >
                        <FundCardAccordions
                            accordions={accordions}
                            onChange={trackTabChange}
                            initiallyOpen={initialTab}
                        />
                    </DocumentContextProvider>
                </FundCardProvider>
            );
        }
    }

    function createTabs(config: Config) {
        return config.tabs.filter((tab) => !tab.parent);
    }

    function createTabContent({
        config,
        tab,
        fund,
        subTabs,
    }: {
        config: Config;
        tab: TabConfig;
        fund: FundDataWithType;
        subTabs?: TabConfig[];
    }) {
        const viewProps = {
            fundId: fund.data.id,
            config,
            tab,
            shareClasses: fund.data.share_classes.map(
                generateLocalId("shareclass"),
            ),
            subTabs,
            groupShareClasses:
                config.productType === "collective" ||
                (fund.type === "pmc" &&
                    fund.data.data.wrappedFund === "true" &&
                    Boolean(tab.groupOn) &&
                    !tab.groups),
        };
        switch (tab.key) {
            case "documents":
                return (
                    <FundCardDocuments
                        fund={fund}
                        hasUpdates={docHasUpdates}
                        markRead={markRead}
                    />
                );
            case "performance":
                viewProps.subTabs = viewProps.subTabs?.map((tab) => {
                    switch (tab.key) {
                        case "summary":
                            return {
                                ...tab,
                                date: dayjs(
                                    isPMC
                                        ? metadata?.quarter_end_date
                                        : metadata?.performance_end_date,
                                ).format("DD MMM YYYY"),
                            };
                        case "discrete":
                            return {
                                ...tab,
                                date: dayjs(metadata?.quarter_end_date).format(
                                    "DD MMM",
                                ),
                            };
                        default:
                            return tab;
                    }
                });

                if (tab.footnote) {
                    return (
                        <WithFootnote
                            footnote={tab.footnote}
                            config={config}
                            fund={fund}
                        >
                            {(footnote) => (
                                <FundCardTable
                                    {...viewProps}
                                    footnote={footnote}
                                    spacedTabs
                                />
                            )}
                        </WithFootnote>
                    );
                }
                return <FundCardTable {...viewProps} spacedTabs />;
            case "listings":
                return <ListingsTabView {...viewProps} />;
            default:
                if (fund.type === "pmc" && tab.key === "prices") {
                    return <FundCardPricesPMC {...viewProps} />;
                }
                return <FundCardTable {...viewProps} />;
        }
    }

    function createAccordionContent({
        config,
        tab,
        fund,
        subTabs,
    }: {
        config: Config;
        tab: TabConfig;
        fund: FundDataWithType;
        subTabs?: TabConfig[];
    }) {
        const viewProps = {
            config,
            tab,
            shareClasses: fund.data.share_classes.map(
                generateLocalId("shareclass"),
            ),
            subTabs,
            groupShareClasses:
                (!isMobile && config.productType === "collective") ||
                (fund.type === "pmc" &&
                    fund.data.data.wrappedFund === "true" &&
                    Boolean(tab.groupOn) &&
                    !tab.groups),
        };
        switch (tab.key) {
            case "documents":
                return [
                    {
                        label: "Key documents",
                        content: <ModalKeyDocumentsSection fund={fund} />,
                    },
                    {
                        label: "Documents by category",
                        content: <AdditionalDocumentsSection column={false} />,
                    },
                ];
            case "performance":
                return [
                    {
                        label: tab.label,
                        content: tab.footnote ? (
                            <WithFootnote
                                footnote={tab.footnote}
                                config={config}
                                fund={fund}
                            >
                                {(footnote) => (
                                    <FundCardTable
                                        {...viewProps}
                                        footnote={footnote}
                                    />
                                )}
                            </WithFootnote>
                        ) : (
                            <FundCardTable {...viewProps} />
                        ),
                        select: viewProps.subTabs?.map((tab) => {
                            switch (tab.key) {
                                case "summary":
                                    return {
                                        label: tab.label,
                                        date: dayjs(
                                            metadata?.performance_end_date,
                                        ).format("DD MMM YYYY"),
                                        value: tab.key,
                                    };
                                case "discrete":
                                    return {
                                        label: tab.label,
                                        date: dayjs(
                                            metadata?.quarter_end_date,
                                        ).format("DD MMM YYYY"),
                                        value: tab.key,
                                    };
                                default:
                                    return {
                                        label: tab.label,
                                        value: tab.key,
                                    };
                            }
                        }),
                    },
                ];
            case "listings":
                return [
                    {
                        label: tab.label,
                        content: <ListingsTabView {...viewProps} />,
                    },
                ];
            default:
                return [
                    {
                        label: tab.label,
                        content: <FundCardTable {...viewProps} />,
                    },
                ];
        }
    }
};

export default FundCardTabs;

function WithFootnote({
    fund,
    footnote,
    config,
    children,
}: {
    footnote: string;
    fund: FundDataWithType;
    config: Config;
    children: (footnote: string | undefined) => JSX.Element;
}) {
    const query = useFootnote({
        audienceId: config.audienceId,
        fundID: fund.data.id,
        partId: footnote,
        routeId: config.routeId,
        languageId: config.languageId,
    });
    return children(query?.data || undefined);
}
